// @ts-nocheck
/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useState,
  useRef,
  useLayoutEffect,
  useMemo,
  useEffect,
} from 'react';
import { Autocomplete, CircularProgress } from '@mui/material';
import { ReactComponent as KeyboardArrowDownIcon } from '../assets/KeyboardArrowDownIcon.svg';
import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';
import debounce from 'lodash/debounce';
import SolyticsBox from './SolyticsBox';
import SolyticsChip from './SolyticsChip';
import SolyticsPopper from './SolyticsPopper';
import SolyticsInput from './SolyticsInput';

// KEY DESIGN TOKENS
const AUTOCOMPLETE_BORDER_RADIUS = 0.5; // 4px
const SMALL_INPUT_PADDING = '6px 12px';
const MEDIUM_INPUT_PADDING = '8px 12px';

/* ------------------ DEFAULT PROPS AS CONSTANTS ------------------ */
const DEFAULT_CONTROLLED = false;
const DEFAULT_MULTIPLE = false;
const DEFAULT_REQUIRED = false;
const DEFAULT_DISABLED = false;
const DEFAULT_ERROR = false;
const DEFAULT_SIZE = 'medium';
const DEFAULT_COLOR = 'primary';
const DEFAULT_DISABLE_CLOSE_ON_SELECT = true;
const DEFAULT_NO_OPTIONS_TEXT = 'No options available.';
const DEFAULT_FULL_WIDTH = false;
const DEFAULT_OPTIONS = [];
const DROPDOWN_PANEL_MAX_HEIGHT = 264;

/* ------------------ UTILITY FUNCTIONS ------------------ */
const truncateText = (text, maxLength) =>
  text && text.length > maxLength ? `${text.substring(0, maxLength)}…` : text;

const AVERAGE_CHARACTER_WIDTH = 8;
const RESERVED_WIDTH_SINGLE_CHIP = 140;
const RESERVED_WIDTH_MULTIPLE_CHIPS = 200;

/**
 * SolyticsAutocomplete - A feature-rich autocomplete/combobox component
 *
 * @component
 * @description
 * SolyticsAutocomplete wraps MUI Autocomplete with SolyticsInput as the renderInput.
 * It supports both local (static options) and controlled (async/server-side) modes,
 * single and multiple selection with chip rendering, select-all, and a chips popover.
 *
 * @param {boolean} [controlled=false] - If true, enables server-side search with fetchData
 * @param {boolean} [multiple=false] - If true, allows selecting multiple values
 * @param {Array<{label: string, value: *}>} [options=[]] - Static options array
 * @param {*} value - Selected value (object for single, array for multiple)
 * @param {function} onChange - Change handler; receives selected value(s)
 * @param {function} [fetchData] - Async function for controlled mode; receives search input string
 * @param {function} [formatOptions] - Transforms fetchData response into { label, value } options
 * @param {string} id - HTML id attribute
 * @param {string} name - Name attribute
 * @param {string} label - Label rendered via SolyticsLabel
 * @param {string} placeholder - Placeholder text (shown when no value selected)
 * @param {boolean} [error=false] - If true, shows error state
 * @param {string} helperText - Helper text below the input
 * @param {boolean} [required=false] - If true, marks field as required
 * @param {boolean} [disabled=false] - If true, disables the component
 * @param {boolean} [fullWidth=false] - If true, takes full container width
 * @param {Object} [infoIcon] - Info icon config: { tooltip, placement?, icon? }
 * @param {function} [getTagChipLabel] - Custom label extractor for selected chips
 * @param {function} [renderOption] - Custom option renderer
 * @param {function} [getOptionLabel] - Custom label extractor for options
 * @param {function} [groupBy] - Groups options by the returned string
 * @param {boolean} [disableClearable=false] - If true, hides the clear button
 * @param {boolean} [disableCloseOnSelect=true] - If true, keeps dropdown open after selection (multi)
 * @param {string} [color='primary'] - Chip color
 * @param {string} [size='small'] - Component size
 * @param {string} [noOptionsText='No options available.'] - Text shown when no options match
 * @param {function} [onBlur] - Blur event handler
 *
 * @returns {React.ReactElement} An autocomplete component
 */

const SolyticsAutocomplete = ({
  // Mode control
  controlled = DEFAULT_CONTROLLED,
  multiple = DEFAULT_MULTIPLE,

  // Data props
  options = DEFAULT_OPTIONS,
  value,
  onChange,

  // Controlled mode props
  fetchData,
  formatOptions,

  // Common props
  id,
  name,
  label,
  placeholder,
  error = DEFAULT_ERROR,
  helperText,
  required = DEFAULT_REQUIRED,
  disabled = DEFAULT_DISABLED,
  fullWidth = DEFAULT_FULL_WIDTH,

  infoIcon,

  // Customization props
  getTagChipLabel,
  renderOption: customRenderOption,
  getOptionLabel: customGetOptionLabel,
  groupBy,
  disableClearable = false,
  disableCloseOnSelect = DEFAULT_DISABLE_CLOSE_ON_SELECT,
  color = DEFAULT_COLOR,
  size = DEFAULT_SIZE,
  noOptionsText = DEFAULT_NO_OPTIONS_TEXT,

  // Event handlers
  onBlur,
}) => {
  // Controlled mode state
  const [open, setOpen] = useState(false);
  const [internalOptions, setInternalOptions] = useState(DEFAULT_OPTIONS);
  const [inputValue, setInputValue] = useState('');
  const [fetching, setFetching] = useState(false);

  // Multiple selection state
  const [chipsPopoverOpen, setChipsPopoverOpen] = useState(false);
  const [componentWidth, setComponentWidth] = useState(0);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const autocompleteRef = useRef(null);

  const loading = open && fetching && controlled;

  // Debounced fetch for controlled mode
  const debouncedFetch = useMemo(
    () =>
      debounce((input) => {
        if (!fetchData) return;
        setFetching(true);
        fetchData(input)
          .then((data) => {
            const formattedData = formatOptions ? formatOptions(data) : data;
            setInternalOptions(formattedData);
          })
          .catch(() => {
            setInternalOptions([]);
          })
          .finally(() => {
            setFetching(false);
          });
      }, 300),
    [fetchData, formatOptions]
  );

  // Initial fetch for controlled mode
  useEffect(() => {
    let active = true;
    if (active && controlled && fetchData && open) {
      setFetching(true);
      fetchData(inputValue)
        .then((data) => {
          if (active) {
            const formattedData = formatOptions ? formatOptions(data) : data;
            setInternalOptions(formattedData);
          }
        })
        .catch(() => {
          setInternalOptions([]);
        })
        .finally(() => {
          if (active) {
            setFetching(false);
          }
        });
    } else if (!controlled) {
      setInternalOptions([]);
    }

    return () => {
      active = false;
    };
  }, [controlled, fetchData, formatOptions, open, inputValue]);

  // Reset options when closed
  useEffect(() => {
    if (!open && controlled) {
      setInternalOptions([]);
      setFetching(false);
    }
  }, [open, controlled]);

  // Cleanup debounced function
  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  // Track component width for chip truncation
  useLayoutEffect(() => {
    const updateWidth = () => {
      if (autocompleteRef.current) {
        setComponentWidth(autocompleteRef.current.offsetWidth);
        setPopoverAnchor(autocompleteRef.current);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [open]);

  // Determine which options to use
  const effectiveOptions = controlled ? internalOptions : options;

  // Select all option for multiple mode
  const selectAll = useMemo(
    () => ({
      label: 'Select all options',
      value: 'mui_auto_complete_select_all',
    }),
    []
  );

  const getOptionsForAutoComplete = useCallback(() => {
    if (!multiple) {
      return effectiveOptions;
    }

    if (customGetOptionLabel || customRenderOption) {
      return effectiveOptions;
    }

    if (effectiveOptions?.length && !controlled) {
      return [selectAll, ...effectiveOptions];
    }

    return effectiveOptions;
  }, [
    multiple,
    effectiveOptions,
    customGetOptionLabel,
    customRenderOption,
    controlled,
    selectAll,
  ]);

  // Handle change
  const handleChange = useCallback(
    (_e, newValue) => {
      if (multiple && newValue?.includes(selectAll)) {
        return onChange?.(effectiveOptions);
      }

      if (multiple && newValue?.length === 0) {
        setOpen(false);
      }

      return onChange?.(newValue);
    },
    [multiple, onChange, effectiveOptions, selectAll]
  );

  // Handle input change for controlled mode
  const handleInputChange = useCallback(
    (_event, newInputValue) => {
      if (controlled) {
        setInputValue(newInputValue);
        debouncedFetch(newInputValue);
      }
    },
    [controlled, debouncedFetch]
  );

  // Option equality check
  const handleAutoCompleteIsOptionEqualToValue = useCallback(
    (item, current) => item?.value === current?.value,
    []
  );

  // Chip delete handler
  const handleChipDelete = useCallback(
    (indexToDelete) => {
      if (!value || !Array.isArray(value)) return;
      const newValue = value.filter((_, index) => index !== indexToDelete);
      onChange?.(newValue);
    },
    [value, onChange]
  );

  const handleDelete = useCallback(
    (onDelete, key) => () => {
      onDelete(key);
    },
    []
  );

  // Render tag for multiple selection
  const renderTagOption = useCallback(
    (option, index, getTagProps) => {
      const { onDelete, ...chipProps } = getTagProps({ index });
      const labelText = getTagChipLabel
        ? getTagChipLabel(option)
        : option?.label;
      const extraWidth =
        value?.length > 1
          ? RESERVED_WIDTH_MULTIPLE_CHIPS
          : RESERVED_WIDTH_SINGLE_CHIP;

      return (
        <SolyticsChip
          color={color}
          size={size}
          shape="pill"
          label={truncateText(
            labelText,
            Math.floor((componentWidth - extraWidth) / AVERAGE_CHARACTER_WIDTH)
          )}
          title={labelText}
          onDelete={handleDelete(onDelete, chipProps.key)}
          {...chipProps}
        />
      );
    },
    [getTagChipLabel, color, size, componentWidth, value, handleDelete]
  );

  // Toggle chips popover
  const toggleChipsPopover = useCallback(
    (e) => {
      if (disabled) return;
      e.stopPropagation();
      e.preventDefault();
      setChipsPopoverOpen((prev) => {
        if (!prev) setOpen(false);
        return !prev;
      });
    },
    [disabled]
  );

  const handleCloseChipsPopover = useCallback(() => {
    setChipsPopoverOpen(false);
  }, []);

  // Render tags for multiple selection
  const renderTags = useCallback(
    (tagValue, getTagProps) => {
      if (!tagValue || tagValue.length === 0) {
        return null;
      }

      const firstChip = renderTagOption(tagValue[0], 0, getTagProps);
      const remainingCount = tagValue.length - 1;

      return (
        <SolyticsBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            overflow: 'hidden',
            paddingLeft: '8px',
          }}
        >
          {firstChip}
          {remainingCount > 0 && (
            <SolyticsBox
              sx={{
                flexShrink: 0,
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <SolyticsChip
                clickable
                onClick={toggleChipsPopover}
                label={`+${remainingCount} more`}
                size={size}
                shape="pill"
                variant="text"
              />
            </SolyticsBox>
          )}
        </SolyticsBox>
      );
    },
    [renderTagOption, toggleChipsPopover, size]
  );

  // Render default option
  const renderDefaultOption = useCallback(
    (liProps, option) => (
      <li
        {...liProps}
        key={option?.value || option?.label || option?.key || option?.id}
      >
        {customGetOptionLabel ? customGetOptionLabel(option) : option?.label}
      </li>
    ),
    [customGetOptionLabel]
  );

  // Render input
  const renderInput = useCallback(
    (params) => {
      // Autocomplete byDefault use InputProps to pass Popup Icon as endAdornment
      const endAdornment = <>{params?.slotProps?.input.endAdornment}</>;

      // Extract and merge params properly
      const { InputLabelProps, InputProps, inputProps, ...otherParams } =
        params;

      return (
        <SolyticsInput
          id={id}
          variant="select"
          {...otherParams}
          label={label}
          name={name}
          required={required}
          placeholder={
            !value || (Array.isArray(value) && !value.length) ? placeholder : ''
          }
          error={Boolean(error)}
          helperText={helperText}
          onBlur={onBlur}
          fullWidth
          disabled={disabled}
          infoIcon={infoIcon}
          slotProps={{
            ...params?.slotProps,
            inputLabel: {
              ...params?.slotProps?.inputLabel,
              shrink: true,
              ...InputLabelProps,
            },
            input: {
              ...params?.slotProps?.input,
              ...InputProps,
              endAdornment: endAdornment,
            },
            htmlInput: {
              ...params?.slotProps?.htmlInput,
              ...inputProps,
              'aria-invalid': Boolean(error),
              'aria-required': required,
            },
          }}
        />
      );
    },
    [
      label,
      id,
      name,
      required,
      placeholder,
      value,
      error,
      helperText,
      onBlur,
      disabled,
      infoIcon,
    ]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  /* ------------------ STYLES ------------------ */
  const sizePadding =
    size === 'small' ? SMALL_INPUT_PADDING : MEDIUM_INPUT_PADDING;

  const autoCompleteSx = useMemo(
    () => (theme) => ({
      '& .MuiAutocomplete-inputRoot': {
        padding: '0 !important',
        paddingRight: '0 !important',
        flexWrap: 'wrap',
        gap: '4px',
      },
      '& .MuiInputBase-input': {
        padding: `${sizePadding} !important`,
        paddingRight: `${disableClearable ? '42px' : '72px'} !important`,
        minWidth: '30px',
        // Allow input to grow as needed
        flex: '1 1 auto',
      },
      '& .MuiInputAdornment-root': {
        // Ensure adornments are visible and properly styled
        marginLeft: 0,
        marginRight: 0,
        height: 'auto',
        maxHeight: 'none',
        // Ensure adornment doesn't shrink
        flexShrink: 0,
      },
      '& .MuiAutocomplete-endAdornment': {
        // Position the icon buttons properly
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        // Ensure they're above other content
        zIndex: 1,
      },
      '& .MuiAutocomplete-popupIndicator': {
        // Style the popup icon button
        padding: '4px',
        width: '24px',
        height: '24px',
        color: theme.palette.action?.active,
        '&:hover': {
          backgroundColor: theme.palette.action?.hover,
        },
      },
      '& .MuiAutocomplete-clearIndicator': {
        // Style the clear icon button
        padding: '4px',
        width: '24px',
        height: '24px',
        marginRight: '-4px',
        color: theme.palette.action?.active,
        '&:hover': {
          backgroundColor: theme.palette.action?.hover,
        },
      },
      '& .MuiAutocomplete-tag': {
        margin: '2px',
        // Ensure chips are visible
        maxWidth: 'calc(100% - 80px)',
      },
    }),
    [disableClearable, sizePadding]
  );

  const autoCompletePaperSx = useMemo(
    () => (theme) => ({
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.grey?.[60] || theme.palette.divider}`,
      borderRadius: AUTOCOMPLETE_BORDER_RADIUS,
      boxShadow: '0px 2px 4px -1px #0000000F, 0px 4px 6px -1px #0000001A',
      overflow: 'hidden',
      '& .MuiAutocomplete-listbox': {
        py: 0,
      },
    }),
    []
  );

  const autoCompleteListboxSx = useMemo(
    () => (theme) => ({
      p: 0,
      m: 0,
      maxHeight: `${DROPDOWN_PANEL_MAX_HEIGHT}px`,
      overflowY: 'auto',
      overflowX: 'hidden',
      '& .MuiAutocomplete-option': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginRight: 0.5,
        marginLeft: 0.5,
        padding: theme.spacing(1.5, 1.5),
        minHeight: 'unset',
        borderRadius: AUTOCOMPLETE_BORDER_RADIUS,
        fontSize: theme.typography.body1.fontSize,
        lineHeight: theme.typography.body1.lineHeight,
        color: theme.palette.text.primary,
        '&:hover': {
          backgroundColor: theme.palette.grey?.[50],
        },
        '&.Mui-focused': {
          backgroundColor: theme.palette.grey?.[50],
        },
        '&[aria-selected="true"]': {
          backgroundColor:
            theme.palette.grey?.[30] || theme.palette.primary?.light,
          fontWeight: 500,
        },
        '&[aria-selected="true"].Mui-focused': {
          backgroundColor: theme.palette.grey?.[50],
        },
      },
    }),
    []
  );

  /* ------------------ MAIN RENDER ------------------ */
  return (
    <SolyticsBox>
      <Autocomplete
        ref={autocompleteRef}
        open={open}
        fullWidth={fullWidth}
        onOpen={handleOpen}
        onClose={handleClose}
        value={multiple ? (Array.isArray(value) ? value : []) : value}
        onChange={handleChange}
        onInputChange={controlled ? handleInputChange : undefined}
        options={getOptionsForAutoComplete()}
        groupBy={groupBy}
        loading={loading}
        loadingText={
          <SolyticsBox sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={20} />
          </SolyticsBox>
        }
        disabled={disabled}
        multiple={multiple}
        disableClearable={disableClearable}
        disableCloseOnSelect={multiple ? disableCloseOnSelect : false}
        size={size}
        popupIcon={<KeyboardArrowDownIcon width={20} height={20} />}
        clearIcon={<CloseIcon width={20} height={20} />}
        isOptionEqualToValue={handleAutoCompleteIsOptionEqualToValue}
        getOptionLabel={
          customGetOptionLabel || ((option) => option?.label || '')
        }
        renderOption={customRenderOption || renderDefaultOption}
        renderInput={renderInput}
        renderValue={multiple ? renderTags : undefined}
        noOptionsText={noOptionsText}
        sx={autoCompleteSx}
        slotProps={{
          popper: {
            placement: 'bottom-start',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 4],
                },
              },
            ],
          },
          listbox: {
            sx: autoCompleteListboxSx,
          },
          paper: {
            sx: autoCompletePaperSx,
          },
        }}
      />

      {multiple && (
        <SolyticsPopper
          enableClickAway
          onClickAway={handleCloseChipsPopover}
          transition
          open={
            chipsPopoverOpen &&
            value &&
            Array.isArray(value) &&
            value.length > 1
          }
          anchorEl={popoverAnchor}
          placement="bottom-start"
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
          ]}
          transitionDuration={200}
        >
          <SolyticsBox
            // Prevent input blur when interacting with the chip popper
            onMouseDown={(e) => e.preventDefault()}
            sx={(theme) => ({
              p: 1,
              maxHeight: '190px',
              overflowY: 'auto',
              overflowX: 'hidden',
              width: componentWidth,
              boxSizing: 'border-box',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              backgroundColor: theme.palette.background.paper || '#FFFFFF',
              borderRadius: AUTOCOMPLETE_BORDER_RADIUS,
              boxShadow:
                '0px 2px 4px -1px #0000000F, 0px 4px 6px -1px #0000001A',
              border: `1px solid ${theme.palette.divider || '#E4E4E7'}`,
              // Custom scrollbar styling - minimal vertical bars without border
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme.palette.grey?.[300] || '#D4D4D4',
                borderRadius: '4px',
                '&:hover': {
                  background: theme.palette.grey?.[400] || '#A3A3A3',
                },
              },
              scrollbarWidth: 'thin',
              scrollbarColor: `${theme.palette.grey?.[300] || '#D4D4D4'} transparent`,
            })}
          >
            {(value ?? []).map((option, index) => {
              if (index === 0) return null;
              return (
                <SolyticsChip
                  key={`popover-chip-${option?.value || option?.label || index}`}
                  color={color}
                  size={size}
                  shape="pill"
                  label={
                    getTagChipLabel ? getTagChipLabel(option) : option?.label
                  }
                  title={
                    getTagChipLabel ? getTagChipLabel(option) : option?.label
                  }
                  onDelete={
                    disabled ? undefined : () => handleChipDelete(index)
                  }
                />
              );
            })}
          </SolyticsBox>
        </SolyticsPopper>
      )}
    </SolyticsBox>
  );
};

export default SolyticsAutocomplete;

/**
 * ## Features
 * - Local mode: pass `options` array for static list
 * - Controlled mode: set `controlled` + `fetchData` for server-side search with debounce (300ms)
 * - Single and multiple selection
 * - Multiple mode shows first chip + "+N more" with expandable popover
 * - Built-in "Select all" option in multiple mode
 * - Loading indicator during async fetch
 * - Custom option rendering and label extraction
 * - Chip truncation based on component width
 * - Full keyboard navigation via MUI Autocomplete
 * - Styled dropdown panel with consistent theme tokens
 * - WCAG 2.1 AA compliant
 *
 * ## Examples
 *
 * ```jsx
 * // Basic single select
 * <SolyticsAutocomplete
 *   name="country"
 *   label="Country"
 *   options={[{ label: 'USA', value: 'us' }, { label: 'UK', value: 'uk' }]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 *
 * // Multiple select with chips
 * <SolyticsAutocomplete
 *   name="tags"
 *   label="Tags"
 *   multiple
 *   options={tagOptions}
 *   value={selectedTags}
 *   onChange={setSelectedTags}
 * />
 *
 * // Server-side search (controlled)
 * <SolyticsAutocomplete
 *   name="users"
 *   label="Search Users"
 *   controlled
 *   fetchData={(query) => api.searchUsers(query)}
 *   formatOptions={(data) => data.map(u => ({ label: u.name, value: u.id }))}
 *   value={selectedUser}
 *   onChange={setSelectedUser}
 * />
 * ```
 */
